import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import "./AuthModal.css";
import logo from "./quixent_updated_logo.png";
import countries from "../data/countries.json";

// Custom phone input component with searchable dropdown
const PhoneInput = ({ value, onChange, name, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Set default country to India
  const defaultCountry =
    countries.find((country) => country.code === "IN") || countries[0];
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);

  // Filter countries based on search term
  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.dial_code.includes(searchTerm)
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePhoneChange = (e) => {
    const phoneNumber = e.target.value.replace(/\D/g, ""); // Remove non-digits
    onChange({
      target: {
        name: name,
        value: phoneNumber,
      },
    });
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchTerm("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="phone-input-container">
      <div className="phone-input-wrapper">
        <div className="country-selector" onClick={() => setIsOpen(!isOpen)}>
          <span className="country-flag">{selectedCountry.flag}</span>
          <span className="country-code">{selectedCountry.dial_code}</span>
          <span className="dropdown-arrow">▼</span>
        </div>
        <input
          ref={inputRef}
          type="tel"
          name={name}
          value={value}
          onChange={handlePhoneChange}
          placeholder={placeholder || "Phone number"}
          className="phone-number-input"
        />
      </div>

      {isOpen && (
        <div className="country-dropdown" ref={dropdownRef}>
          <div className="search-container">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search country..."
              className="country-search"
              autoFocus
            />
          </div>
          <div className="country-list">
            {filteredCountries.map((country) => (
              <div
                key={country.code}
                className={`country-item ${
                  selectedCountry.code === country.code ? "selected" : ""
                }`}
                onClick={() => handleCountrySelect(country)}
              >
                <span className="country-flag">{country.flag}</span>
                <span className="country-name">{country.name}</span>
                <span className="country-dial-code">{country.dial_code}</span>
              </div>
            ))}
            {filteredCountries.length === 0 && (
              <div className="no-results">No countries found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const AuthModal = ({ isOpen, onClose, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const passwordInputRef = useRef(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    countryCode: "+91", // Default to India
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        email: formData.email,
      });

      setSuccessMessage(
        "Password reset link sent to your email. Please check your inbox."
      );
      // Clear the email field
      setFormData((prev) => ({ ...prev, email: "" }));
    } catch (err) {
      console.error("Forgot password error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to send reset email. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    // Basic validation
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters long");
        return;
      }
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        // Handle login
        const response = await axios.post(`${API_URL}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });

        const { token, data: user } = response.data;
        localStorage.setItem("token", token);
        onAuthSuccess(user);
      } else {
        // First register the user
        const response = await axios.post(
          `${API_URL}/auth/register`,
          {
            username: formData.username,
            email: formData.email,
            phone: formData.phone || "",
            password: formData.password,
          },
          {
            validateStatus: function (status) {
              return status < 500; // Reject only if status is greater than or equal to 500
            },
          }
        );

        // Check for error response
        if (response.data && !response.data.success) {
          throw new Error(response.data.message || "Registration failed");
        }

        // Show the message from server (includes email status)
        alert(response.data.message);

        // Reset form and switch to login
        setIsLogin(true);
        setFormData({
          username: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: "",
        });
      }
    } catch (err) {
      console.error("Authentication error:", err);
      // Extract the error message from the response or use the error message
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "An error occurred. Please try again.";

      // Display the error message to the user
      setError(errorMessage);

      // If it's a validation error, log the full error for debugging
      if (err.response?.status === 400) {
        console.error("Validation error details:", err.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generatePassword = useCallback(() => {
    const length = 12;
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]\\:;"<>,.?/=';
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  }, []);

  const handleGeneratePassword = useCallback(
    (e) => {
      e.preventDefault();
      const newPassword = generatePassword();
      setGeneratedPassword(newPassword);
      setShowPasswordPopup(true);
    },
    [generatePassword]
  );

  const handleUsePassword = useCallback(() => {
    setShowPasswordPopup(false);
    setFormData((prev) => ({
      ...prev,
      password: generatedPassword,
      confirmPassword: generatedPassword,
    }));
    if (passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [generatedPassword]);

  const regeneratePassword = useCallback(() => {
    const newPassword = generatePassword();
    setGeneratedPassword(newPassword);
    setFormData((prev) => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword,
    }));
  }, [generatePassword]);

  const switchMode = (e) => {
    e.preventDefault();
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setError("");
    setSuccessMessage("");
    setShowPasswordPopup(false);
    setFormData({
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setIsForgotPassword(true);
    setIsLogin(false);
    setError("");
    setSuccessMessage("");
    setFormData((prev) => ({
      ...prev,
      password: "",
      confirmPassword: "",
    }));
  };

  const handleBackToLogin = (e) => {
    e.preventDefault();
    setIsForgotPassword(false);
    setIsLogin(true);
    setError("");
    setSuccessMessage("");
    setFormData({
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });
  };

  // When the modal opens, try to prefill email from URL query param ?email=...
  useEffect(() => {
    if (!isOpen) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const emailFromUrl = params.get("email");
      if (emailFromUrl) {
        setFormData((prev) => ({ ...prev, email: emailFromUrl }));
        // Optionally clean the email param from URL to avoid keeping PII in address bar
        const url = new URL(window.location.href);
        url.searchParams.delete("email");
        window.history.replaceState(
          {},
          document.title,
          url.pathname + url.search
        );
      }
    } catch (_) {}
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <motion.div
        className="auth-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
      >
        {showPasswordPopup && (
          <div className="password-popup-overlay">
            <div className="password-popup">
              <h3>Secure Password Generated</h3>
              <div className="generated-password">
                {generatedPassword}
                <button
                  className="copy-password-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedPassword);
                    alert("Password copied to clipboard!");
                  }}
                >
                  Copy
                </button>
              </div>
              <div className="password-popup-actions">
                <button className="regenerate-btn" onClick={regeneratePassword}>
                  Regenerate
                </button>
                <button
                  className="use-password-btn"
                  onClick={handleUsePassword}
                >
                  Use This Password
                </button>
              </div>
            </div>
          </div>
        )}
        <button className="close-button" onClick={onClose}>
          &times;
        </button>

        <div className="auth-header">
          <img src={logo} alt="Quixent Logo" className="auth-logo" />
          <h2>
            {isForgotPassword
              ? "Reset Password"
              : isLogin
              ? "Welcome Back"
              : "Create Account"}
          </h2>
          <p className="auth-subtitle">
            {isForgotPassword
              ? "Enter your email to receive a password reset link"
              : isLogin
              ? "Login to access your account"
              : "Create an account to get started"}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        {isForgotPassword ? (
          // Forgot Password Form
          <form onSubmit={handleForgotPassword} className="auth-form">
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <button
              type="submit"
              className={`auth-submit-btn ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </button>

            <div className="auth-footer">
              <button
                type="button"
                className="switch-mode-btn"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : (
          // Login/Signup Form
          <form onSubmit={handleSubmit} className="auth-form">
            <AnimatePresence mode="wait">
              {!isLogin ? (
                // Signup Form Fields
                <>
                  <div className="form-group">
                    <input
                      type="text"
                      name="username"
                      placeholder="Username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <PhoneInput
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number"
                    />
                  </div>

                  <div className="form-group">
                    <input
                      ref={passwordInputRef}
                      type="password"
                      name="password"
                      placeholder="Create Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <div className="password-helper">
                      <button
                        type="button"
                        className="generate-password-link"
                        onClick={handleGeneratePassword}
                      >
                        Generate a secure password
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </>
              ) : (
                // Login Form Fields
                <>
                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="password"
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Forgot Password Link */}
                  <div className="forgot-password">
                    <button
                      type="button"
                      className="forgot-password-link"
                      onClick={handleForgotPasswordClick}
                      disabled={isLoading}
                    >
                      Forgot Password?
                    </button>
                  </div>
                </>
              )}

              <button
                type="submit"
                className={`auth-submit-btn ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : isLogin ? "Login" : "Sign Up"}
              </button>
            </AnimatePresence>
          </form>
        )}

        {!isForgotPassword && (
          <div className="auth-footer">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              className="switch-mode-btn"
              onClick={switchMode}
              disabled={isLoading}
            >
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AuthModal;
